<template>
  <!-- Modal: `scrollable` makes the overlay scroll and align, the panel an ordinary child of it. Non-modal: no scrim, the page stays live. -->
  <UModal
    :open="modelValue"
    :title="title"
    :description="description"
    :close="showCloseButton"
    :content="contentProps"
    :ui="ui"
    :modal="modal"
    :overlay="modal"
    :scrollable="modal"
    @update:open="onOpenChange"
    @after:leave="onAfterLeave"
  >
    <template
      v-if="$slots['titleIcon']"
      #title
    >
      <slot name="titleIcon" />{{ title }}
    </template>

    <template
      v-if="$slots['content']"
      #content
    >
      <slot name="content" />
    </template>

    <template
      v-else
      #body
    >
      <slot />
    </template>

    <template
      v-if="$slots['footer']"
      #footer
    >
      <slot name="footer" />
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { computed, onBeforeUpdate, ref, useSlots } from 'vue'
import type { ModalProps } from '@nuxt/ui'
import UModal from '@nuxt/ui/components/Modal.vue'
import { useOverlayShell } from '~/composables/useOverlayShell'
import type { OverlayShellEvents } from '~/types/overlays/OverlayShellEvents'

interface Props {
  /** Blocks the page behind the dialog; false leaves the page interactive and drops the scrim. */
  modal?: boolean
  /** Controls whether the modal is open. */
  modelValue: boolean
  /** Modal heading text. */
  title: string
  /** Optional supporting text below the heading. */
  description?: string
  /** Tailwind classes controlling the modal max width. */
  maxWidthClass?: string
  /** Makes the modal content fill most of the mobile viewport height. */
  fullHeightOnMobile?: boolean
  /** Expands the modal to the full viewport on mobile. */
  fullScreenOnMobile?: boolean
  /** Vertically centers the modal on mobile. */
  centerOnMobile?: boolean
  /** Pins the modal to the top of the viewport, for content whose height changes. */
  alignTop?: boolean
  /** Allows closing when the backdrop is clicked. */
  closeOnOutsideClick?: boolean
  /** Controls visibility of the close button. */
  showCloseButton?: boolean
  /** Extra classes applied to the panel. */
  panelClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  closeOnOutsideClick: true,
  description: '',
  maxWidthClass: 'max-w-3xl',
  modal: true,
  panelClass: '',
  showCloseButton: true
})

const emit = defineEmits<OverlayShellEvents>()

defineSlots<{
  default?: () => unknown
  footer?: () => unknown
  titleIcon?: () => unknown
  /** Replaces the whole panel, for a dialog that brings its own layout. */
  content?: () => unknown
}>()

const slots = useSlots()
const hasContentSlot = ref(slots['content'] !== undefined)
onBeforeUpdate(() => {
  hasContentSlot.value = slots['content'] !== undefined
})

// `scrollable` makes the overlay the scrolling, aligning box, so alignment lives on the overlay.
const overlayAlignment = computed(() => {
  if (props.alignTop) return 'items-start p-4 md:p-20 sm:p-6'
  return props.centerOnMobile ? 'items-center' : 'items-end sm:items-center'
})

const ui = computed<NonNullable<ModalProps['ui']>>(() => {
  const scrollingBody = props.fullScreenOnMobile || props.fullHeightOnMobile
  return {
    body: scrollingBody ? 'min-h-0 overflow-y-auto' : '',
    content: [
      props.maxWidthClass,
      hasContentSlot.value ? 'overflow-hidden' : '',
      props.fullScreenOnMobile
        ? 'border-0 h-dvh max-h-dvh rounded-none sm:border sm:h-auto sm:max-h-none sm:rounded-2xl sm:w-[calc(100vw-2rem)] w-screen'
        : '',
      props.fullHeightOnMobile && !props.fullScreenOnMobile
        ? 'h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] sm:h-auto sm:max-h-none'
        : '',
      props.panelClass
    ],
    overlay: [overlayAlignment.value, props.fullScreenOnMobile ? 'p-0 sm:p-4 sm:py-8' : '']
  }
})

const { onOpenChange, onAfterLeave, contentProps } = useOverlayShell({
  closeOnOutsideClick: () => props.closeOnOutsideClick,
  emit,
  isModal: () => props.modal,
  isOpen: () => props.modelValue
})
</script>
